import { type DefaultContext, gqlPromise, mutationPromise } from '@egw/clients'
import { type QueryClient } from '@tanstack/react-query'
import { createMutationHook } from '../../../hooks'
import {
  type InvestmentAccountDetail,
  type UpdateInvestmentAccountNameInput,
  type UpdateInvestmentAccountNameOutput,
} from '../../../models'
import { invalidateInvestmentAccountUpdateQueries } from '../../../utils'

/**
 * Get Investment Account Detail
 * @param alias - number
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<InvestmentAccountDetail>
 * @example const investmentAccountDetail = await getInvestmentAccountDetail();
 */
export const getInvestmentAccountDetail = async (
  alias: number,
  context?: DefaultContext,
): Promise<InvestmentAccountDetail> => {
  const response = (await gqlPromise(
    {
      name: 'investmentAccountDetail',
      query: `
        query investmentAccountDetail($alias: Alias!) {
          investmentAccountDetail(alias: $alias) {
            alias
            accountNumber
            accountName
            status
            balance {
              investedBalance {
                amount
                currencyCode
              }
              accountValue {
                amount
                currencyCode
              }
              cashBalance {
                amount
                currencyCode
              }
              pendingWithdrawal {
                amount
                currencyCode
              }
              availableForWithdrawal {
                amount
                currencyCode
              }
              amountSinceInception {
                amount
                currencyCode
              }
            }
            ... on InvestmentBrokerageAccount {
              __typename
              accountTitle
              attributes {
                minimumDeposit
                minimumDepositMet
                acatAllowed
                clientWithdrawalAllowed
                clientDepositAllowed
              }
              assetAllocation {
                preference {
                  equities
                  fixedIncome
                  cashEquivalent
                }
                other {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    symbol
                    name
                  }
                }
                equities {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    symbol
                    name
                  }
                  sectors {
                    sector
                    percentage
                    amount {
                      currencyCode
                      amount
                    }
                    holdings {
                      amount {
                        currencyCode
                        amount
                      }
                      quantity
                      costBasis {
                        currencyCode
                        amount
                      }
                      symbol
                      name
                    }
                  }
                }
                fixedIncome {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    costBasis {
                      currencyCode
                      amount
                    }
                    symbol
                    name
                  }
                  categories {
                    category
                    percentage
                    amount {
                      currencyCode
                      amount
                    }
                    holdings {
                      amount {
                        currencyCode
                        amount
                      }
                      quantity
                      costBasis {
                        currencyCode
                        amount
                      }
                      symbol
                      name
                    }
                  }
                }
                cashEquivalent {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    costBasis {
                      currencyCode
                      amount
                    }
                    symbol
                    name
                  }
                  categories {
                    category
                    percentage
                    amount {
                      currencyCode
                      amount
                    }
                    holdings {
                      amount {
                        currencyCode
                        amount
                      }
                      quantity
                      costBasis {
                        currencyCode
                        amount
                      }
                      symbol
                      name
                    }
                  }
                }
              }
            }
            ... on InvestmentIRAAccount {
              __typename
              accountTitle
              attributes {
                minimumDeposit
                minimumDepositMet
                iraWithdrawal {
                  withdrawalAmount
                  distributionType
                }
                acatAllowed
                clientWithdrawalAllowed
                clientDepositAllowed
              }
              retirementContribution {
                annualContributionsLimit
                remainingAnnualLimit
              }
              assetAllocation {
                preference {
                  equities
                  fixedIncome
                  cashEquivalent
                }
                other {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    symbol
                    name
                  }
                }
                equities {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    costBasis {
                      currencyCode
                      amount
                    }
                    symbol
                    name
                  }
                  sectors {
                    sector
                    percentage
                    amount {
                      currencyCode
                      amount
                    }
                    holdings {
                      amount {
                        currencyCode
                        amount
                      }
                      quantity
                      costBasis {
                        currencyCode
                        amount
                      }
                      symbol
                      name
                    }
                  }
                }
                fixedIncome {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    costBasis {
                      currencyCode
                      amount
                    }
                    symbol
                    name
                  }
                  categories {
                    category
                    percentage
                    amount {
                      currencyCode
                      amount
                    }
                    holdings {
                      amount {
                        currencyCode
                        amount
                      }
                      quantity
                      costBasis {
                        currencyCode
                        amount
                      }
                      symbol
                      name
                    }
                  }
                }
                cashEquivalent {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    costBasis {
                      currencyCode
                      amount
                    }
                    symbol
                    name
                  }
                  categories {
                    category
                    percentage
                    amount {
                      currencyCode
                      amount
                    }
                    holdings {
                      amount {
                        currencyCode
                        amount
                      }
                      quantity
                      costBasis {
                        currencyCode
                        amount
                      }
                      symbol
                      name
                    }
                  }
                }
              }
            }
            ... on InvestmentRothIRAAccount {
              __typename
              accountTitle
              attributes {
                minimumDeposit
                minimumDepositMet
                iraWithdrawal {
                  withdrawalAmount
                  distributionType
                }
                acatAllowed
                clientWithdrawalAllowed
                clientDepositAllowed
              }
              retirementContribution {
                annualContributionsLimit
                remainingAnnualLimit
              }
              assetAllocation {
                preference {
                  equities
                  fixedIncome
                  cashEquivalent
                }
                other {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    symbol
                    name
                  }
                }
                equities {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    costBasis {
                      currencyCode
                      amount
                    }
                    symbol
                    name
                  }
                  sectors {
                    sector
                    percentage
                    amount {
                      currencyCode
                      amount
                    }
                    holdings {
                      amount {
                        currencyCode
                        amount
                      }
                      quantity
                      costBasis {
                        currencyCode
                        amount
                      }
                      symbol
                      name
                    }
                  }
                }
                fixedIncome {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    costBasis {
                      currencyCode
                      amount
                    }
                    symbol
                    name
                  }
                  categories {
                    category
                    percentage
                    amount {
                      currencyCode
                      amount
                    }
                    holdings {
                      amount {
                        currencyCode
                        amount
                      }
                      quantity
                      costBasis {
                        currencyCode
                        amount
                      }
                      symbol
                      name
                    }
                  }
                }
                cashEquivalent {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    costBasis {
                      currencyCode
                      amount
                    }
                    symbol
                    name
                  }
                  categories {
                    category
                    percentage
                    amount {
                      currencyCode
                      amount
                    }
                    holdings {
                      amount {
                        currencyCode
                        amount
                      }
                      quantity
                      costBasis {
                        currencyCode
                        amount
                      }
                      symbol
                      name
                    }
                  }
                }
              }
            }
            ... on InvestmentBrokerageCompanionAccount {
              __typename
              accountTitle
              attributes {
                acatAllowed
                clientWithdrawalAllowed
                clientDepositAllowed
              }
              holdings {
                name
                symbol
                costBasis {
                  currencyCode
                  amount
                }
                amount {
                  currencyCode
                  amount
                }
                quantity
              }
            }
            ... on InvestmentIRACompanionAccount {
              __typename
              accountTitle
              holdings {
                name
                symbol
                costBasis {
                  currencyCode
                  amount
                }
                amount {
                  currencyCode
                  amount
                }
                quantity
              }
            }
            ... on InvestmentRothIRACompanionAccount {
              __typename
              accountTitle
              holdings {
                name
                symbol
                costBasis {
                  currencyCode
                  amount
                }
                amount {
                  currencyCode
                  amount
                }
                quantity
              }
            }
            ... on InvestmentInheritedIRAAccount {
              __typename
              accountTitle
              attributes {
                minimumDeposit
                minimumDepositMet
                acatAllowed
                clientWithdrawalAllowed
                clientDepositAllowed
                iraWithdrawal {
                  withdrawalAmount
                  distributionType
                }
              }
              assetAllocation {
                preference {
                  equities
                  fixedIncome
                  cashEquivalent
                }
                other {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    symbol
                    name
                  }
                }
                equities {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    costBasis {
                      currencyCode
                      amount
                    }
                    symbol
                    name
                  }
                  sectors {
                    sector
                    percentage
                    amount {
                      currencyCode
                      amount
                    }
                    holdings {
                      amount {
                        currencyCode
                        amount
                      }
                      quantity
                      costBasis {
                        currencyCode
                        amount
                      }
                      symbol
                      name
                    }
                  }
                }
                fixedIncome {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    costBasis {
                      currencyCode
                      amount
                    }
                    symbol
                    name
                  }
                  categories {
                    category
                    percentage
                    amount {
                      currencyCode
                      amount
                    }
                    holdings {
                      amount {
                        currencyCode
                        amount
                      }
                      quantity
                      costBasis {
                        currencyCode
                        amount
                      }
                      symbol
                      name
                    }
                  }
                }
                cashEquivalent {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    costBasis {
                      currencyCode
                      amount
                    }
                    symbol
                    name
                  }
                  categories {
                    category
                    percentage
                    amount {
                      currencyCode
                      amount
                    }
                    holdings {
                      amount {
                        currencyCode
                        amount
                      }
                      quantity
                      costBasis {
                        currencyCode
                        amount
                      }
                      symbol
                      name
                    }
                  }
                }
              }
            }

            ... on InvestmentJointBrokerageTenantsInCommonAccount {
              __typename
              accountTitle
              attributes {
                minimumDeposit
                minimumDepositMet
                acatAllowed
                clientWithdrawalAllowed
                clientDepositAllowed
                iraWithdrawal {
                  withdrawalAmount
                  distributionType
                }
              }
              assetAllocation {
                preference {
                  equities
                  fixedIncome
                  cashEquivalent
                }
                other {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    symbol
                    name
                  }
                }
                equities {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    costBasis {
                      currencyCode
                      amount
                    }
                    symbol
                    name
                  }
                  sectors {
                    sector
                    percentage
                    amount {
                      currencyCode
                      amount
                    }
                    holdings {
                      amount {
                        currencyCode
                        amount
                      }
                      quantity
                      costBasis {
                        currencyCode
                        amount
                      }
                      symbol
                      name
                    }
                  }
                }
                fixedIncome {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    costBasis {
                      currencyCode
                      amount
                    }
                    symbol
                    name
                  }
                  categories {
                    category
                    percentage
                    amount {
                      currencyCode
                      amount
                    }
                    holdings {
                      amount {
                        currencyCode
                        amount
                      }
                      quantity
                      costBasis {
                        currencyCode
                        amount
                      }
                      symbol
                      name
                    }
                  }
                }
                cashEquivalent {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    costBasis {
                      currencyCode
                      amount
                    }
                    symbol
                    name
                  }
                  categories {
                    category
                    percentage
                    amount {
                      currencyCode
                      amount
                    }
                    holdings {
                      amount {
                        currencyCode
                        amount
                      }
                      quantity
                      costBasis {
                        currencyCode
                        amount
                      }
                      symbol
                      name
                    }
                  }
                }
              }
            }

            ... on InvestmentJointBrokerageTenantsInCommonCompanionAccount {
              __typename
              accountTitle
              attributes {
                minimumDeposit
                minimumDepositMet
                acatAllowed
                clientWithdrawalAllowed
                clientDepositAllowed
                iraWithdrawal {
                  withdrawalAmount
                  distributionType
                }
              }
              holdings {
                name
                symbol
                costBasis {
                  currencyCode
                  amount
                }
                amount {
                  currencyCode
                  amount
                }
                quantity
              }
            }

            ... on InvestmentJointBrokerageTenantsWithRightOfSurvivorshipAccount {
              __typename
              accountTitle
              attributes {
                minimumDeposit
                minimumDepositMet
                acatAllowed
                clientWithdrawalAllowed
                clientDepositAllowed
                iraWithdrawal {
                  withdrawalAmount
                  distributionType
                }
              }
              assetAllocation {
                preference {
                  equities
                  fixedIncome
                  cashEquivalent
                }
                other {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    symbol
                    name
                  }
                }
                equities {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    costBasis {
                      currencyCode
                      amount
                    }
                    symbol
                    name
                  }
                  sectors {
                    sector
                    percentage
                    amount {
                      currencyCode
                      amount
                    }
                    holdings {
                      amount {
                        currencyCode
                        amount
                      }
                      quantity
                      costBasis {
                        currencyCode
                        amount
                      }
                      symbol
                      name
                    }
                  }
                }
                fixedIncome {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    costBasis {
                      currencyCode
                      amount
                    }
                    symbol
                    name
                  }
                  categories {
                    category
                    percentage
                    amount {
                      currencyCode
                      amount
                    }
                    holdings {
                      amount {
                        currencyCode
                        amount
                      }
                      quantity
                      costBasis {
                        currencyCode
                        amount
                      }
                      symbol
                      name
                    }
                  }
                }
                cashEquivalent {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    costBasis {
                      currencyCode
                      amount
                    }
                    symbol
                    name
                  }
                  categories {
                    category
                    percentage
                    amount {
                      currencyCode
                      amount
                    }
                    holdings {
                      amount {
                        currencyCode
                        amount
                      }
                      quantity
                      costBasis {
                        currencyCode
                        amount
                      }
                      symbol
                      name
                    }
                  }
                }
              }
            }

            ... on InvestmentJointBrokerageTenantsWithRightOfSurvivorshipCompanionAccount {
              __typename
              accountTitle
              attributes {
                minimumDeposit
                minimumDepositMet
                acatAllowed
                clientWithdrawalAllowed
                clientDepositAllowed
                iraWithdrawal {
                  withdrawalAmount
                  distributionType
                }
              }
              holdings {
                name
                symbol
                costBasis {
                  currencyCode
                  amount
                }
                amount {
                  currencyCode
                  amount
                }
                quantity
              }
            }

            ... on InvestmentInheritedRothIRAAccount {
              __typename
              accountTitle
              attributes {
                minimumDeposit
                minimumDepositMet
                acatAllowed
                clientWithdrawalAllowed
                clientDepositAllowed
                iraWithdrawal {
                  withdrawalAmount
                  distributionType
                }
              }
              assetAllocation {
                preference {
                  equities
                  fixedIncome
                  cashEquivalent
                }
                other {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    symbol
                    name
                  }
                }
                equities {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    costBasis {
                      currencyCode
                      amount
                    }
                    symbol
                    name
                  }
                  sectors {
                    sector
                    percentage
                    amount {
                      currencyCode
                      amount
                    }
                    holdings {
                      amount {
                        currencyCode
                        amount
                      }
                      quantity
                      costBasis {
                        currencyCode
                        amount
                      }
                      symbol
                      name
                    }
                  }
                }
                fixedIncome {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    costBasis {
                      currencyCode
                      amount
                    }
                    symbol
                    name
                  }
                  categories {
                    category
                    percentage
                    amount {
                      currencyCode
                      amount
                    }
                    holdings {
                      amount {
                        currencyCode
                        amount
                      }
                      quantity
                      costBasis {
                        currencyCode
                        amount
                      }
                      symbol
                      name
                    }
                  }
                }
                cashEquivalent {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    costBasis {
                      currencyCode
                      amount
                    }
                    symbol
                    name
                  }
                  categories {
                    category
                    percentage
                    amount {
                      currencyCode
                      amount
                    }
                    holdings {
                      amount {
                        currencyCode
                        amount
                      }
                      quantity
                      costBasis {
                        currencyCode
                        amount
                      }
                      symbol
                      name
                    }
                  }
                }
              }
            }

            ... on InvestmentJointBrokerageTenantsByEntiretyAccount {
              __typename
              accountTitle
              attributes {
                minimumDeposit
                minimumDepositMet
                acatAllowed
                clientWithdrawalAllowed
                clientDepositAllowed
                iraWithdrawal {
                  withdrawalAmount
                  distributionType
                }
              }
              assetAllocation {
                preference {
                  equities
                  fixedIncome
                  cashEquivalent
                }
                other {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    symbol
                    name
                  }
                }
                equities {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    costBasis {
                      currencyCode
                      amount
                    }
                    symbol
                    name
                  }
                  sectors {
                    sector
                    percentage
                    amount {
                      currencyCode
                      amount
                    }
                    holdings {
                      amount {
                        currencyCode
                        amount
                      }
                      quantity
                      costBasis {
                        currencyCode
                        amount
                      }
                      symbol
                      name
                    }
                  }
                }
                fixedIncome {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    costBasis {
                      currencyCode
                      amount
                    }
                    symbol
                    name
                  }
                  categories {
                    category
                    percentage
                    amount {
                      currencyCode
                      amount
                    }
                    holdings {
                      amount {
                        currencyCode
                        amount
                      }
                      quantity
                      costBasis {
                        currencyCode
                        amount
                      }
                      symbol
                      name
                    }
                  }
                }
                cashEquivalent {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    costBasis {
                      currencyCode
                      amount
                    }
                    symbol
                    name
                  }
                  categories {
                    category
                    percentage
                    amount {
                      currencyCode
                      amount
                    }
                    holdings {
                      amount {
                        currencyCode
                        amount
                      }
                      quantity
                      costBasis {
                        currencyCode
                        amount
                      }
                      symbol
                      name
                    }
                  }
                }
              }
            }

            ... on InvestmentJointBrokerageCommunityPropertyAccount {
              __typename
              accountTitle
              attributes {
                minimumDeposit
                minimumDepositMet
                acatAllowed
                clientWithdrawalAllowed
                clientDepositAllowed
                iraWithdrawal {
                  withdrawalAmount
                  distributionType
                }
              }
              assetAllocation {
                preference {
                  equities
                  fixedIncome
                  cashEquivalent
                }
                other {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    symbol
                    name
                  }
                }
                equities {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    costBasis {
                      currencyCode
                      amount
                    }
                    symbol
                    name
                  }
                  sectors {
                    sector
                    percentage
                    amount {
                      currencyCode
                      amount
                    }
                    holdings {
                      amount {
                        currencyCode
                        amount
                      }
                      quantity
                      costBasis {
                        currencyCode
                        amount
                      }
                      symbol
                      name
                    }
                  }
                }
                fixedIncome {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    costBasis {
                      currencyCode
                      amount
                    }
                    symbol
                    name
                  }
                  categories {
                    category
                    percentage
                    amount {
                      currencyCode
                      amount
                    }
                    holdings {
                      amount {
                        currencyCode
                        amount
                      }
                      quantity
                      costBasis {
                        currencyCode
                        amount
                      }
                      symbol
                      name
                    }
                  }
                }
                cashEquivalent {
                  __typename
                  amount {
                    currencyCode
                    amount
                  }
                  percentage
                  holdings {
                    amount {
                      currencyCode
                      amount
                    }
                    quantity
                    costBasis {
                      currencyCode
                      amount
                    }
                    symbol
                    name
                  }
                  categories {
                    category
                    percentage
                    amount {
                      currencyCode
                      amount
                    }
                    holdings {
                      amount {
                        currencyCode
                        amount
                      }
                      quantity
                      costBasis {
                        currencyCode
                        amount
                      }
                      symbol
                      name
                    }
                  }
                }
              }
            }

            ... on InvestmentJointBrokerageTenantsByEntiretyCompanionAccount {
              __typename
              accountTitle
              attributes {
                minimumDeposit
                minimumDepositMet
                acatAllowed
                clientWithdrawalAllowed
                clientDepositAllowed
                iraWithdrawal {
                  withdrawalAmount
                  distributionType
                }
              }
              holdings {
                name
                symbol
                costBasis {
                  currencyCode
                  amount
                }
                amount {
                  currencyCode
                  amount
                }
                quantity
              }
            }


            ... on InvestmentJointBrokerageCommunityPropertyCompanionAccount {
              __typename
              accountTitle
              attributes {
                minimumDeposit
                minimumDepositMet
                acatAllowed
                clientWithdrawalAllowed
                clientDepositAllowed
                iraWithdrawal {
                  withdrawalAmount
                  distributionType
                }
              }
              holdings {
                name
                symbol
                costBasis {
                  currencyCode
                  amount
                }
                amount {
                  currencyCode
                  amount
                }
                quantity
              }
            }
          }
        }`,
    },
    { alias },
    context,
  )) as { investmentAccountDetail: InvestmentAccountDetail }
  return response.investmentAccountDetail
}

export const updateInvestmentAccountName = async (
  updateInvestmentAccountNameInput: UpdateInvestmentAccountNameInput,
  context?: DefaultContext,
  queryClient?: QueryClient,
): Promise<UpdateInvestmentAccountNameOutput> => {
  const response = (await mutationPromise(
    {
      name: 'updateInvestmentAccountName',
      query: `
        mutation updateInvestmentAccountName($updateInvestmentAccountNameInput: UpdateInvestmentAccountNameInput!) {
          updateInvestmentAccountName(updateInvestmentAccountNameInput: $updateInvestmentAccountNameInput) {
            ... on UpdateInvestmentAccountName {
              __typename
              isSuccessful
            }
            ... on UpdateInvestmentAccountNameError {
              __typename
              errorType
            }
          }
        }
      `,
    },
    { updateInvestmentAccountNameInput },
    context,
  )) as { updateInvestmentAccountName: UpdateInvestmentAccountNameOutput }

  const result = response.updateInvestmentAccountName

  // Invalidate queries on successful investment account name update if queryClient is provided
  if (queryClient && result.__typename === 'UpdateInvestmentAccountName' && result.isSuccessful) {
    invalidateInvestmentAccountUpdateQueries({
      client: queryClient,
      investmentAccountAlias: updateInvestmentAccountNameInput.alias,
      refetchType: 'all',
    })
  }

  return result
}

export const useUpdateInvestmentAccountName = createMutationHook<
  UpdateInvestmentAccountNameInput,
  UpdateInvestmentAccountNameOutput
>(updateInvestmentAccountName)
